import type { Canvas } from 'fabric';

type DisposableFabricCanvas = {
	dispose: () => ReturnType<Canvas['dispose']> | void;
};

const hasProperty = <PropertyName extends string>(
	value: object,
	propertyName: PropertyName,
): value is Record<PropertyName, unknown> => {
	return propertyName in value;
};

const isPromiseLike = (value: unknown): value is Promise<unknown> => {
	return !!value &&
		typeof value === 'object' &&
		hasProperty(value, 'catch') &&
		typeof value.catch === 'function';
};

const isDetachedCanvasDomError = (error: unknown): boolean => {
	return !!error &&
		typeof error === 'object' &&
		hasProperty(error, 'name') &&
		error.name === 'NotFoundError';
};

export const disposeFabricCanvasSafely = (canvas: DisposableFabricCanvas): void => {
	try {
		const disposeResult = canvas.dispose();
		if (isPromiseLike(disposeResult)) {
			disposeResult.catch((error) => {
				if (!isDetachedCanvasDomError(error)) {
					console.warn('Unexpected Fabric canvas dispose error:', error);
				}
			});
		}
	} catch (error) {
		if (!isDetachedCanvasDomError(error)) {
			throw error;
		}
	}
};

const isFabricCanvasContainer = (element: HTMLElement | null): element is HTMLElement => {
	return !!(
		element &&
		(
			element.classList.contains('canvas-container') ||
			element.getAttribute('data-fabric') === 'wrapper'
		)
	);
};

export const restoreFabricCanvasElement = (
	canvasElement: HTMLCanvasElement | null,
	fabricContainer: HTMLElement | null = null,
): HTMLCanvasElement | null => {
	if (!canvasElement) return null;

	const container = isFabricCanvasContainer(fabricContainer)
		? fabricContainer
		: canvasElement.parentElement;

	if (isFabricCanvasContainer(container) && container.parentNode) {
		container.parentNode.replaceChild(canvasElement, container);
	}

	canvasElement.classList.remove('lower-canvas');
	canvasElement.removeAttribute('data-fabric');

	return canvasElement;
};

export const getFabricCanvasContainer = (canvas: Canvas): HTMLElement | null => {
	const { elements } = canvas;

	if (!('container' in elements) || !(elements.container instanceof HTMLElement)) {
		return null;
	}

	return elements.container;
};
